import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Select, SvgIcon } from "@mui/material";
import { dropKeys } from "@bryxinc/ts-utils";
import BryxColors from "../types/theme";
import BryxErrorText from "./BryxErrorText";
import { styled } from "@mui/system";
import withLabel from "../hocs/withLabel";
import BryxBox from "./BryxBox";
/**
 * A visually identical component to {@link BryxDropdown}, except with an abstracted `onClick`, event, with no dropdown children.
 * This is useful to create a {@link Popover} or {@link BryxPopper} with a trigger that visually resembles a {@link BryxDropdown}.
 */
const BryxDropdownButton = styled(({ active, value, onClick, error, errorText, label, ...rest }) => {
    return (_jsxs(BryxBox, { isFlex: true, column: true, children: [_jsx(Select, { ...dropKeys(rest, ["minWidth"]), onClick: onClick, size: "medium", autoWidth: true, open: false, error: !!error, value: value ?? "", label: label, IconComponent: () => (_jsx(SvgIcon, { viewBox: "-4 0 16 6", sx: {
                        position: "absolute",
                        right: "8px",
                        cursor: "pointer",
                        width: "16px",
                        height: "16px",
                        pointerEvents: "none",
                        color: BryxColors.lightNotify("inactive"),
                        transform: active ? "rotate(-180deg)" : "rotate(0deg)",
                    }, children: _jsx("path", { d: "M1.22595 0.722096L4.00358 3.66819L6.78121 0.722096C7.0604 0.425968 7.51141 0.425968 7.7906 0.722096C8.0698 1.01822 8.0698 1.49658 7.7906 1.79271L4.5047 5.2779C4.2255 5.57403 3.7745 5.57403 3.4953 5.2779L0.209396 1.79271C-0.0697987 1.49658 -0.0697987 1.01822 0.209396 0.722096C0.488591 0.433561 0.946756 0.425968 1.22595 0.722096Z" }) })), children: _jsx("option", { value: value ?? "", children: value ?? "" }) }), _jsx(BryxErrorText, { error: errorText })] }));
})(({ theme, minWidth }) => {
    const palette = theme.palette.mode;
    const brand1 = BryxColors.brand1(palette);
    const brand4 = BryxColors.brand4(palette);
    const brandDark2 = BryxColors.brandDark2(palette);
    const notifyInactive = BryxColors.notify(palette, "inactive");
    return {
        height: "46px",
        minWidth: minWidth ?? "361px",
        color: notifyInactive,
        "& ~ .MuiTypography-root": {
            color: notifyInactive,
            "&:hover": {
                color: brand1,
            },
        },
        "& .MuiOutlinedInput-notchedOutline": {
            border: `1px solid ${notifyInactive}`,
        },
        // Focus (clicked and/or open)
        "&.Mui-focused, &.Mui-focused > *": {
            color: palette == "light" ? brandDark2 : brand4,
            borderColor: palette == "light" ? brandDark2 : brand4,
            fill: palette == "light" ? brandDark2 : brand4,
        },
        "&.Mui-focused > .MuiSvgIcon-root, &[aria-expanded=true] > .MuiSvgIcon-root": {
            transform: "rotate(-180deg)",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: `1px solid ${palette == "light" ? brandDark2 : brand4}`,
        },
        "&.Mui-focused ~ .MuiTypography-root": {
            color: palette == "light" ? brandDark2 : brand4,
        },
        // Hover, not focused
        "&:hover": {
            color: brand1,
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            color: brand1,
            borderColor: brand1,
        },
        "&:hover .MuiSvgIcon-root": {
            fill: brand1,
        },
        "&:hover ~ .MuiTypography-root": {
            color: brand1,
        },
        // Hover and focused
        "&.Mui-focused:hover, &.Mui-focused:hover > *": {
            color: brand1,
            borderColor: brand1,
            fill: brand1,
        },
    };
});
export default withLabel(BryxDropdownButton);
